import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CASE_QUERY_KEYS } from 'app/modules/cases/queries/keys';
import { CaseSearchPayload } from 'app/modules/search/payload';
import { CaseSearchV2Response, CaseSource } from 'app/modules/search/response';
import { withSearchErrorToast } from 'app/modules/search/utils';
import { selectHasReadCasesPermissions } from 'app/modules/session/selectors';
import { post } from 'app/shared/utils/fetchr';
import { unionBy } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const getCaseSources = (data: CaseSearchV2Response): CaseSource[] =>
  data.hits.map((hit) => hit.source);

export const searchCase = (payload?: CaseSearchPayload) =>
  post<CaseSearchV2Response>('/v1/cases/search/v2', payload);

export const useCaseSearch = (
  payload?: CaseSearchPayload,
  enabled: boolean = true,
) => {
  const queryClient = useQueryClient();
  const hasReadCasesPermissions = useSelector(selectHasReadCasesPermissions);

  const query = useQuery<CaseSearchV2Response, Error, CaseSource[]>({
    enabled: Boolean(enabled && !!payload && hasReadCasesPermissions),
    // can't garbage collect since we return all transactions in the query history
    gcTime: Infinity,
    queryFn: withSearchErrorToast(() => searchCase(payload)),
    queryKey: CASE_QUERY_KEYS.getCaseSearch(payload),
    select: (data) => getCaseSources(data),
    staleTime: 5 * 60 * 1000,
  });

  const cachedQueryData = queryClient.getQueriesData<CaseSearchV2Response>({
    queryKey: CASE_QUERY_KEYS.getCaseSearch(),
  });

  const cases = useMemo(
    () =>
      cachedQueryData.reduce<CaseSource[]>((acc, i) => {
        const [, queryData] = i;
        if (queryData) {
          return unionBy(getCaseSources(queryData), acc, 'id');
        }
        return acc;
      }, []),

    [cachedQueryData],
  );

  return {
    query,
    cases,
  };
};
